import React, { useEffect, useState } from 'react';
import FormRadioGroup from 'Shared/components/FormComponents/FormRadioGroup';
import { Field } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';

import { required } from 'Shared/utils/formValidations';
import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';
import { useToggle, useFieldValue } from 'Shared/utils/hooks';

import { reminderTypeOptions } from '../constants';
import { formatOptionsFromArray } from './utils';

import { getUserById } from 'Shared/selectors/workspace';

//Actions
import { fetchGoals } from 'Shared/actions/Goals.actions';

//Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import WorkspaceField from './PrivacyRadioGroup/WorkspaceField/WorkspaceField';
import ParentGoalField from '../ParentGoalField/ParentGoalField';
import WpAlert from 'Wpds/components/alert/WpAlert';

import './PrivacyAndReminders.scss';

type Props = {
  isEdit: boolean;
  workspaceSlug?: string;
  toggleEdit: Function;
};

export default function PrivacyAndRemindersV2({ isEdit, workspaceSlug, toggleEdit }: Props) {
  const dispatch = useDispatch();
  const assigneeId = useFieldValue('assigneeId');
  const workspaceId = useFieldValue('workspaceId');
  const [showAddUserToWorkspaceAlert, setShowAddUserToWorkspaceAlert] = useState(false);

  const parentId = useFieldValue('parentId');
  const [showParentGoalSelect, toggleShowParentGoalSelect] = useToggle(!!parentId);

  useEffect(() => {
    async function fetchData() {
      await dispatch(fetchGoals({ context: 'team', completed: false }));
    }

    fetchData();
  }, []);

  const workspaces = useSelector(state => state.workspaces);
  const assigneeFirstName = useSelector(getUserById(assigneeId))?.firstName;

  useEffect(() => {
    if (workspaceId) {
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (workspace) {
        setShowAddUserToWorkspaceAlert(!workspace.users.find(u => u.id === assigneeId));
      }
    } else {
      setShowAddUserToWorkspaceAlert(false);
    }
  }, [workspaces, workspaceId]);

  const reminderOptions = formatOptionsFromArray(reminderTypeOptions);
  const privacyOptions = [
    { label: 'Everyone in my organization', value: 'public' },
    { label: 'Just members of the workspace', value: 'private' },
  ];

  const labelRequired = '*required';
  const labelClassName = 'goals-privacy-field-label';
  const fieldClassName = 'goals-privacy-field-group';
  return (
    <div>
      {!isEdit && (
        <FieldGroup label="Add a parent goal" labelClassName={labelClassName} className={fieldClassName}>
          {!showParentGoalSelect && (
            <WpButton variant="tertiary" onClick={toggleShowParentGoalSelect}>
              <WpIcon name="link" /> Link to an existing goal
            </WpButton>
          )}
          {showParentGoalSelect && <ParentGoalField />}
        </FieldGroup>
      )}
      <FieldGroup
        label="Select a workspace for this goal to live in"
        labelClassName={labelClassName}
        required
        labelRequired={labelRequired}
        className={fieldClassName}
      >
        <WorkspaceField name="workspaceId" oneOnOneUserId={assigneeId} disabled={isEdit} />
        {showAddUserToWorkspaceAlert && (
          <div className="goal-workspace-alert">
            <WpAlert showIcon={false}>
              {assigneeFirstName} is not in this workspace. We&apos;ll add them for you.
            </WpAlert>
          </div>
        )}
      </FieldGroup>
      <FieldGroup
        label="Who can view this goal?"
        labelClassName={labelClassName}
        required
        labelRequired={labelRequired}
        className={fieldClassName}
      >
        <Field
          name="privacy"
          options={privacyOptions}
          component={FormRadioGroup}
          validate={required}
          isEdit={isEdit}
          toggleEdit={toggleEdit}
          workspaceSlug={workspaceSlug}
        />
      </FieldGroup>
      <FieldGroup
        label="How often do you want to check in on this goal?"
        labelClassName={labelClassName}
        labelRequired={labelRequired}
        description="You’ll get an in-app notification, and a discussion topic will be added to the selected workspace"
        className={fieldClassName}
      >
        <Field name="reminder" options={reminderOptions} component={FormRadioGroup} />
      </FieldGroup>
    </div>
  );
}
